import { CONCEPT_VALUE_ALL_OPTION } from "../constants";
import {
  ReferenceItemsRnCell,
  type ReferenceItemDetail,
} from "../TaggingPlan/ReferenceItemsCell";
import type { EnhancedTaggingRuleRow } from "./types";

const REFERENCE_DOC_TYPE_DISPLAY = "Doc Type";

/**
 * Build Conditional Classification Reference Items cards (R1, …) from
 * object type / DocType subtype fields on the list row.
 */
export function buildEnhancedReferenceItemDetails(
  row: EnhancedTaggingRuleRow,
): ReferenceItemDetail[] {
  const objectName = row.objectTypeName?.trim() || "";
  if (!objectName) return [];

  if (row.referenceDimensions && row.referenceDimensions.length > 0) {
    return row.referenceDimensions.map((dim, index) => ({
      label: `R${index + 1}`,
      object: objectName,
      dimension: dim.conceptDisplayName?.trim() || dim.conceptCode.trim(),
      value: dim.allValues
        ? CONCEPT_VALUE_ALL_OPTION.name
        : dim.values
            .map((item) => item.name?.trim() || item.code)
            .filter(Boolean)
            .join(", "),
    }));
  }

  const subTypeName = row.objectSubTypeName?.trim() || "";
  if (!subTypeName) {
    return [
      {
        label: "R1",
        object: objectName,
        dimension: "",
        value: "",
      },
    ];
  }

  const nameParts = subTypeName
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);
  const codeParts = (row.objectSubType || "")
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);
  const isAll =
    (codeParts.length === 1 &&
      codeParts[0]!.toLowerCase() ===
        CONCEPT_VALUE_ALL_OPTION.code.toLowerCase()) ||
    nameParts.some(
      (name) =>
        name.toLowerCase() === CONCEPT_VALUE_ALL_OPTION.name.toLowerCase(),
    );

  return [
    {
      label: "R1",
      object: objectName,
      dimension: REFERENCE_DOC_TYPE_DISPLAY,
      value: isAll ? CONCEPT_VALUE_ALL_OPTION.name : nameParts.join(", "),
    },
  ];
}

type EnhancedReferenceItemsCellProps = Readonly<{
  row: EnhancedTaggingRuleRow;
}>;

/** Conditional Classification table Reference Items — dashed R1 + hover popover. */
export default function EnhancedReferenceItemsCell({
  row,
}: EnhancedReferenceItemsCellProps) {
  return (
    <ReferenceItemsRnCell details={buildEnhancedReferenceItemDetails(row)} />
  );
}
